import type { ReactNode } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type { ZuubPatientDetailDataV4 } from '../../@types/zuub/contracts';
import { generateFileName } from '../../utils/file';

export interface GenerateBenefitsPdfRequest {
  /** Capture the benefits DOM and return a PDF blob (matches the page). Required at runtime. */
  getBlob?: () => Promise<Blob>;
  /** Optional plan (V4) for metadata; blob is always from getBlob (DOM capture). */
  plan?: ZuubPatientDetailDataV4;
  patientName?: string;
  unifiedAppointmentId: string;
}

export interface GenerateBenefitsPdfResponse {
  success: boolean;
  message: ReactNode;
}

/**
 * Generates the Benefits PDF on the client (matching the /benefits page) and triggers download.
 */
export const useGenerateBenefitsPdf = (
  options?: Parameters<
    typeof useMutation<
      GenerateBenefitsPdfResponse,
      AxiosError,
      GenerateBenefitsPdfRequest
    >
  >[0],
) => {
  const { t } = useLingui();

  return useMutation({
    mutationFn: async (
      request: GenerateBenefitsPdfRequest,
    ): Promise<GenerateBenefitsPdfResponse> => {
      if (!request.getBlob) {
        throw new Error('getBlob is required to generate benefits PDF');
      }
      const blob = await request.getBlob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = generateFileName(
        'benefits-summary',
        request.unifiedAppointmentId,
        'pdf',
        request.patientName,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: t`PDF downloaded successfully` };
    },
    ...options,
  });
};
